import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

/**
 * Marcadores y controles de mapa que comparten los reportes de seguimiento del gestor.
 *
 * Los marcadores se dibujan en el navegador en lugar de usar imagenes: los empaquetadores
 * rompen las rutas de los iconos por defecto de Leaflet, y traerlos de un CDN dejaria el
 * mapa sin marcadores cada vez que el servidor no tenga salida a internet.
 */

// El pulso se registra una sola vez en el documento: Leaflet inserta el marcador como HTML
// crudo y no hay donde declarar la animacion desde el estilo en linea.
const PULSO = "gestor-pulso";

if (typeof document !== "undefined" && document.getElementById(PULSO) === null) {
  const estilo = document.createElement("style");
  estilo.id = PULSO;
  estilo.innerHTML = `@keyframes ${PULSO}{0%{transform:scale(.5);opacity:.75}100%{transform:scale(2.4);opacity:0}}`;
  document.head.appendChild(estilo);
}

/** Hito: un lugar donde el gestor hizo algo. */
export const icono = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};width:18px;height:18px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.6)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12],
  });

// Marcador de incidencia: triangulo de advertencia, distinto de los puntos normales para
// que un corte no se confunda con una visita.
export const iconoAlerta = () =>
  L.divIcon({
    className: "",
    html: `<div style="width:0;height:0;border-left:11px solid transparent;border-right:11px solid transparent;border-bottom:19px solid #d32f2f;filter:drop-shadow(0 0 2px rgba(0,0,0,.6))"></div>`,
    iconSize: [22, 19],
    iconAnchor: [11, 19],
    popupAnchor: [0, -20],
  });

/**
 * Donde esta el gestor. Es una gota anclada en la punta y no un circulo como los hitos,
 * para que se distinga de un solo golpe de vista y para que no tape el marcador del punto
 * sobre el que cae. El halo late solo cuando la señal es reciente: un pin parpadeando
 * sobre una posicion de hace tres horas diria que ahi esta, y no es cierto.
 */
export const iconoGestor = (color, latiendo) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:34px;height:44px">
      ${
        latiendo
          ? `<div style="position:absolute;left:5px;top:5px;width:24px;height:24px;border-radius:50%;background:${color};animation:${PULSO} 1.8s ease-out infinite"></div>`
          : ""
      }
      <div style="position:absolute;left:5px;top:2px;width:24px;height:24px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 0 5px rgba(0,0,0,.6)"></div>
      <div style="position:absolute;left:14px;top:11px;width:6px;height:6px;border-radius:50%;background:#fff"></div>
    </div>`,
    iconSize: [34, 44],
    iconAnchor: [17, 32],
    popupAnchor: [0, -30],
  });

/**
 * Parada numerada de una ruta. El numero va dentro del marcador y no en una etiqueta
 * aparte porque el orden ES el reporte: sin el numero encima, un mapa con cuarenta puntos
 * no dice en que direccion recorrerlos.
 */
export const iconoParada = (numero, color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};width:24px;height:24px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.6);color:#fff;font-size:12px;font-weight:bold;line-height:20px;text-align:center;font-family:sans-serif">${numero}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });

// Arranque de la jornada, para que se lea en que direccion va el recorrido.
export const iconoInicio = () =>
  L.divIcon({
    className: "",
    html: `<div style="background:#fff;width:14px;height:14px;border-radius:50%;border:4px solid #1769aa;box-shadow:0 0 4px rgba(0,0,0,.5)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });

/**
 * El center de MapContainer solo aplica al montar, asi que mover la vista despues necesita
 * el mapa ya creado. Se reacciona a una señal y no a la posicion en si: mientras no llegue
 * un punto nuevo, el mapa se queda donde el usuario lo dejo.
 */
export const CentrarEn = ({ posicion, senal }) => {
  const mapa = useMap();

  useEffect(() => {
    if (posicion === null) {
      return;
    }
    mapa.setView(posicion, Math.max(mapa.getZoom(), 15), { animate: true });
  }, [senal]);

  return null;
};

/**
 * Encuadra el mapa para que entren todas las posiciones. Con un solo gestor no se ajusta
 * nada: el encuadre de un punto suelto deja el mapa pegado al maximo acercamiento, donde
 * no se distingue la ciudad.
 */
export const AjustarVista = ({ posiciones, senal }) => {
  const mapa = useMap();

  useEffect(() => {
    if (posiciones.length === 0) {
      return;
    }

    if (posiciones.length === 1) {
      mapa.setView(posiciones[0], 15, { animate: true });
      return;
    }

    mapa.fitBounds(L.latLngBounds(posiciones), { padding: [40, 40] });
  }, [senal]);

  return null;
};
